'use client';

import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface CrossfadeVideoProps {
  /** Path under /public to the .mp4 to play. */
  src: string;
  /** Optional CSS mask to apply to the video (e.g. radial vignette). */
  maskImage?: string;
  /** Overlap window (seconds) where both layers are visible during the swap. */
  crossfadeSeconds?: number;
  /** Tailwind classes appended to the outer container. */
  className?: string;
}

/**
 * A looping background video that swaps between two stacked `<video>` layers
 * so the loop point is invisible — there's no hard cut at the end of the clip.
 *
 *  - Starts the first time it scrolls into view.
 *  - Loops *seamlessly* via opacity crossfade between two `<video>`s.
 *  - Completely non-interactive (no controls, no click handling).
 *  - Right-click menu is suppressed.
 */
export const CrossfadeVideo = ({
  src,
  maskImage,
  crossfadeSeconds = 0.8,
  className,
}: CrossfadeVideoProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoARef = useRef<HTMLVideoElement>(null);
  const videoBRef = useRef<HTMLVideoElement>(null);

  const [hasStarted, setHasStarted] = useState(false);
  const [activeIsA, setActiveIsA] = useState(true);
  const swappingRef = useRef(false);

  // Start the first clip when the wrapper enters the viewport.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasStarted) {
          const a = videoARef.current;
          if (a) {
            try {
              a.currentTime = 0;
            } catch {
              /* ignore */
            }
            const p = a.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
          setHasStarted(true);
        }
      },
      { threshold: 0.3 },
    );

    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [hasStarted]);

  const handleTimeUpdate = (which: 'A' | 'B') => () => {
    const isThisActive = (which === 'A') === activeIsA;
    if (!isThisActive) return;
    if (swappingRef.current) return;

    const current = which === 'A' ? videoARef.current : videoBRef.current;
    const other = which === 'A' ? videoBRef.current : videoARef.current;
    if (!current || !other) return;
    const duration = current.duration;
    if (!duration || !isFinite(duration)) return;

    if (current.currentTime >= duration - crossfadeSeconds) {
      swappingRef.current = true;
      try {
        other.currentTime = 0;
      } catch {
        /* ignore */
      }
      const p = other.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      setActiveIsA((prev) => !prev);
      window.setTimeout(
        () => {
          swappingRef.current = false;
        },
        crossfadeSeconds * 1000 + 100,
      );
    }
  };

  const sharedStyle = maskImage
    ? { WebkitMaskImage: maskImage, maskImage }
    : undefined;
  const sharedClass =
    'pointer-events-none block w-full select-none transition-opacity duration-700 ease-out';

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      {/* Layer A — sets the box's height via its intrinsic aspect ratio. */}
      <video
        ref={videoARef}
        src={src}
        muted
        playsInline
        preload="metadata"
        aria-hidden
        onContextMenu={(e) => e.preventDefault()}
        onTimeUpdate={handleTimeUpdate('A')}
        disablePictureInPicture
        controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
        className={cn(sharedClass, activeIsA ? 'opacity-100' : 'opacity-0')}
        style={sharedStyle}
      />
      {/* Layer B — stacked, crossfades in as A nears its end. Mounted only
          once A has actually started: until then it is a second full decode of
          a clip nobody is watching. Layer A has to stay, since its intrinsic
          aspect ratio is what gives the box its height. */}
      {hasStarted && (
        <video
          ref={videoBRef}
          src={src}
          muted
          playsInline
          preload="metadata"
          aria-hidden
          onContextMenu={(e) => e.preventDefault()}
          onTimeUpdate={handleTimeUpdate('B')}
          disablePictureInPicture
          controlsList="nodownload noplaybackrate noremoteplayback nofullscreen"
          className={cn(
            'absolute inset-0 h-full',
            sharedClass,
            activeIsA ? 'opacity-0' : 'opacity-100',
          )}
          style={sharedStyle}
        />
      )}
    </div>
  );
};

export default CrossfadeVideo;
